import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import { trackPageView, bindAutoClickTracking } from "@/lib/tracking";

import Landing from "@/pages/Landing";
import Booking from "@/pages/Booking";
import AIAssistant from "@/pages/AIAssistant";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PublicProposal from "@/pages/PublicProposal";
import PublicInstallment from "@/pages/PublicInstallment";
import PublicAppt from "@/pages/PublicAppt";
import StagePage from "@/pages/StagePage";
import ServicePage from "@/pages/ServicePage";
import ServicesIndex from "@/pages/ServicesIndex";
import PortalAuth from "@/pages/PortalAuth";
import PortalResetPassword from "@/pages/PortalResetPassword";
import PortalHome from "@/pages/PortalHome";
import { CaseStudiesList, CaseStudyDetail } from "@/pages/CaseStudies";
import LegalPage from "@/pages/LegalPage";
import AdminEntrepreneurs from "@/pages/admin/Entrepreneurs";
import AdminCaseStudies from "@/pages/admin/CaseStudies";
import AdminLegal from "@/pages/admin/Legal";
import AdminLogin from "@/pages/admin/Login";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminLeads from "@/pages/admin/Leads";
import AdminProjects from "@/pages/admin/Projects";
import AdminAppointments from "@/pages/admin/Appointments";
import AdminTestimonials from "@/pages/admin/Testimonials";
import AdminSettings from "@/pages/admin/Settings";
import AdminServices from "@/pages/admin/Services";
import AdminProposals from "@/pages/admin/Proposals";
import ProposalEdit from "@/pages/admin/ProposalEdit";
import AdminInvoices from "@/pages/admin/Invoices";
import AdminSubscriptions from "@/pages/admin/Subscriptions";
import AdminAppointmentTypes from "@/pages/admin/AppointmentTypes";
import AdminDiagnostics from "@/pages/admin/Diagnostics";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminEmailsBackup from "@/pages/admin/EmailsBackup";
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import About from "@/pages/About";

function Tracker() {
  const loc = useLocation();
  useEffect(() => {
    trackPageView(loc.pathname);
  }, [loc.pathname]);
  useEffect(() => {
    bindAutoClickTracking();
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Skip if URL has a hash anchor (e.g. /#contact)
    if (window.location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Tracker />
        <ScrollToTop />
        <main id="main-content" tabIndex="-1">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/qui-sommes-nous" element={<About />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/etape/:stageId" element={<StagePage />} />
          <Route path="/services/:serviceId" element={<ServicePage />} />
          <Route path="/services" element={<ServicesIndex />} />
          <Route path="/nos-services" element={<ServicesIndex />} />
          <Route path="/portail/connexion" element={<PortalAuth />} />
          <Route path="/portail/reinitialiser" element={<PortalResetPassword />} />
          <Route path="/portail" element={<PortalHome />} />
          <Route path="/espace-client" element={<PortalHome />} />
          <Route path="/espace-entreprise" element={<PortalHome />} />
          <Route path="/etudes-de-cas" element={<CaseStudiesList />} />
          <Route path="/etudes-de-cas/:slug" element={<CaseStudyDetail />} />
          <Route path="/cgv" element={<LegalPage />} />
          <Route path="/mentions-legales" element={<LegalPage />} />
          <Route path="/confidentialite" element={<LegalPage />} />
          <Route path="/rendez-vous" element={<Booking />} />
          <Route path="/rdv/:id" element={<PublicAppt />} />
          <Route path="/assistant-ia" element={<AIAssistant />} />
          <Route path="/devis/:token" element={<PublicProposal />} />
          <Route path="/payer/echeance/:pid/:idx" element={<PublicInstallment />} />
          <Route path="/paiement/succes" element={<PaymentSuccess />} />
          <Route path="/paiement/annule" element={<PaymentCancel />} />

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="/admin/projets" element={<AdminProjects />} />
            <Route path="/admin/rendez-vous" element={<AdminAppointments />} />
            <Route path="/admin/services" element={<AdminServices />} />
            <Route path="/admin/devis" element={<AdminProposals />} />
            <Route path="/admin/devis/nouveau" element={<ProposalEdit />} />
            <Route path="/admin/devis/:id" element={<ProposalEdit />} />
            <Route path="/admin/factures" element={<AdminInvoices />} />
            <Route path="/admin/abonnements" element={<AdminSubscriptions />} />
            <Route path="/admin/types-rdv" element={<AdminAppointmentTypes />} />
            <Route path="/admin/temoignages" element={<AdminTestimonials />} />
            <Route path="/admin/entrepreneurs" element={<AdminEntrepreneurs />} />
            <Route path="/admin/etudes-de-cas" element={<AdminCaseStudies />} />
            <Route path="/admin/legal" element={<AdminLegal />} />
            <Route path="/admin/parametres" element={<AdminSettings />} />
            <Route path="/admin/diagnostic" element={<AdminDiagnostics />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/emails" element={<AdminEmailsBackup />} />
          </Route>
        </Routes>
        </main>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
